import { useState, useEffect, useCallback, useMemo } from 'react'
import { useGet, usePost } from '../utils/apiComponent/useApi'
import { apiClient } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { showSuccessToast, showErrorToast } from '../utils/toast'

/**
 * Hook pour envoyer un message de contact (public)
 */
export function useContactForm() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { execute: sendMessageApi, loading: apiLoading } = usePost(
    endpoints.sendContactMessage,
    { immediate: false, isPublicRoute: true }
  )

  useEffect(() => {
    setLoading(apiLoading)
  }, [apiLoading])

  const sendMessage = useCallback(async (messageData) => {
    setLoading(true)
    setError(null)

    try {
      const response = await sendMessageApi({
        fullName: messageData.fullName,
        email: messageData.email,
        subject: messageData.subject,
        message: messageData.message
      })

      const successMessage = response?.messages?.[0] || 'Votre message a été envoyé avec succès. Nous vous répondrons rapidement.'
      showSuccessToast({ message: successMessage })
      
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.'
      setError(errorMessage)
      showErrorToast({ message: errorMessage })
      throw err
    } finally {
      setLoading(false)
    }
  }, [sendMessageApi])

  return {
    sendMessage,
    loading,
    error
  }
}

/**
 * Hook pour gérer les messages de contact (admin)
 */
export function useContactManagement(options = {}) {
  const {
    page = 1,
    limit = 10,
    search = '',
    isRead = null,
    isReplied = null,
    email = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
    immediate = false
  } = options

  const [messages, setMessages] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })

  // Construire l'URL avec les paramètres de requête
  const messagesUrl = useMemo(() => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    if (search) params.append('search', search)
    if (isRead !== null) params.append('isRead', isRead.toString())
    if (isReplied !== null) params.append('isReplied', isReplied.toString())
    if (email) params.append('email', email)
    params.append('sortBy', sortBy)
    params.append('sortOrder', sortOrder)
    return `${endpoints.getContactMessages}?${params.toString()}`
  }, [page, limit, search, isRead, isReplied, email, sortBy, sortOrder])

  const { data: messagesData, loading: messagesLoading, error: messagesError, execute: fetchMessages } = useGet(
    messagesUrl,
    { immediate }
  )

  const { data: statsData, loading: statsLoading, error: statsError, execute: fetchStats } = useGet(
    endpoints.getContactStats,
    { immediate }
  )


  useEffect(() => {
    setLoading(messagesLoading || statsLoading)
    setError(messagesError || statsError)
  }, [messagesLoading, statsLoading, messagesError, statsError])

  useEffect(() => {
    if (messagesData) {
      if (messagesData.messages && Array.isArray(messagesData.messages)) {
        setMessages(messagesData.messages)
        setPagination({
          page: messagesData.page || page,
          limit: messagesData.limit || limit,
          total: messagesData.total || 0,
          totalPages: messagesData.totalPages || 0
        })
      } else if (Array.isArray(messagesData)) {
        setMessages(messagesData)
      }
    }
  }, [messagesData, page, limit])

  useEffect(() => {
    if (statsData) {
      setStats(statsData)
    }
  }, [statsData])

  const markAsRead = useCallback(async (messageId) => {
    try {
      const response = await apiClient(endpoints.markContactAsRead(messageId), 'PATCH', {})
      showSuccessToast({ message: 'Message marqué comme lu' })
      fetchMessages()
      fetchStats()
      return response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de la mise à jour du message'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchMessages, fetchStats])

  const markAsUnread = useCallback(async (messageId) => {
    try {
      const response = await apiClient(endpoints.markContactAsUnread(messageId), 'PATCH', {})
      showSuccessToast({ message: 'Message marqué comme non lu' })
      fetchMessages()
      fetchStats()
      return response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de la mise à jour du message'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchMessages, fetchStats])

  const replyToMessage = useCallback(async (messageId, replyMessage) => {
    try {
      const response = await apiClient(endpoints.replyToContact(messageId), 'POST', {
        body: { replyMessage }
      })
      const successMessage = response?.messages?.[0] || 'Réponse envoyée avec succès'
      showSuccessToast({ message: successMessage })
      fetchMessages()
      fetchStats()
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de l\'envoi de la réponse'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchMessages, fetchStats])

  const deleteMessage = useCallback(async (messageId) => {
    try {
      await apiClient(endpoints.deleteContactMessage(messageId), 'DELETE', {})
      showSuccessToast({ message: 'Message supprimé avec succès' })
      fetchMessages()
      fetchStats()
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de la suppression du message'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchMessages, fetchStats])

  return {
    messages,
    stats,
    loading,
    error,
    pagination,
    fetchMessages,
    fetchStats,
    markAsRead,
    markAsUnread,
    replyToMessage,
    deleteMessage
  }
}

/**
 * Hook pour récupérer un message de contact spécifique
 */
export function useContactMessage(messageId, options = {}) {
  const { immediate = false } = options
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: messageData, loading: messageLoading, error: messageError, execute: fetchMessage } = useGet(
    messageId ? endpoints.getContactMessage(messageId) : null,
    { immediate: immediate && !!messageId }
  )

  useEffect(() => {
    setLoading(messageLoading)
    setError(messageError)
  }, [messageLoading, messageError])

  useEffect(() => {
    if (messageData) {
      setMessage(messageData)
    }
  }, [messageData])

  return {
    message,
    loading,
    error,
    fetchMessage
  }
}
